import { SizeTypeLookupOption, SizeTypeOption } from 'types/size-types';
import { authorizedFetch, serverMessage } from './authorized-fetch';
import fetchWithRetry from './fetch-retry';

const BASE_URL = '/api/size-types';

/* thrown when POST hits the UNIQUE(code) constraint (backend returns 409) so the
 * admin UI can prompt to reactivate the existing (possibly inactive) row instead */
export class DuplicateSizeTypeCodeError extends Error {
  constructor(public code?: string) {
    super(`Size type with code '${code ?? ''}' already exists`);
    this.name = 'DuplicateSizeTypeCodeError';
  }
}

/*
 * A catalog write the server refused, with its own reason: e.g. the fleet dependency 409 naming the
 * mapping codes that still target a row. Writes must never fail silently — the admin would believe a
 * deactivation happened that did not.
 */
export class SizeTypeSaveError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'SizeTypeSaveError';
  }
}

const saveError = async (response: Response): Promise<SizeTypeSaveError> =>
  new SizeTypeSaveError(
    response.status,
    (await serverMessage(response)) || `The size type could not be saved (${response.status}).`
  );

/*
 * Every catalog row, retired ones flagged `active: false`, readable by every role. Lookups (class,
 * label, reefer) use this so a gate user and an admin read historical codes the same way (F14).
 * An older backend ignores the parameter and returns active rows only, which is the old behaviour.
 */
export const getSizeTypeCatalog = async (): Promise<SizeTypeOption[]> =>
  await fetchWithRetry<SizeTypeOption[]>(`${BASE_URL}/options?includeInactive=true`, { method: 'GET' }, []);

/* active options only, sorted by label by the backend — carries equipmentType, feeds dropdowns */
export const getSizeTypeOptions = async (): Promise<SizeTypeOption[]> =>
  await fetchWithRetry<SizeTypeOption[]>(`${BASE_URL}/options`, { method: 'GET' }, []);

/* full rows incl. inactive — feeds the admin grid */
export const getSizeTypeList = async (): Promise<SizeTypeLookupOption[]> =>
  await fetchWithRetry<SizeTypeLookupOption[]>(BASE_URL, { method: 'GET' }, []);

export const saveSizeType = async (option: Partial<SizeTypeLookupOption>): Promise<SizeTypeLookupOption> => {
  const response = await fetch(BASE_URL, {
    method: 'POST',
    body: JSON.stringify(option),
    headers: new Headers({
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    })
  });
  if (response.status === 409) throw new DuplicateSizeTypeCodeError(option.code);
  if (!response.ok) throw await saveError(response);
  return response.json();
};

/*
 * PUT preserves the existing code — omit it from the body (backend ignores it regardless). Throws a
 * `SizeTypeSaveError` on refusal instead of resolving quietly.
 */
export const updateSizeType = async (option: SizeTypeLookupOption): Promise<SizeTypeLookupOption> => {
  const { code, ...body } = option;
  const response = await authorizedFetch(new URL(`${BASE_URL}/${option.id}`, window.location.origin), 'PUT', body);
  if (!response.ok) throw await saveError(response);
  return response.json();
};

/* soft delete — backend sets active=false. Throws a `SizeTypeSaveError` on refusal. */
export const deleteSizeType = async (id: number): Promise<void> => {
  const response = await authorizedFetch(new URL(`${BASE_URL}/${id}`, window.location.origin), 'DELETE');
  if (!response.ok) throw await saveError(response);
};
